import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import AppContext from "../utils/appContext";
import { useNavigate } from "react-router-dom";
import { authApi } from "../api";
import { getStoredUserEmail, storeSession } from "../lib/auth";
import { Button, Input, Spinner } from "../components/ui";

const Login: React.FC = () => {
  const [email, setEmail] = useState(getStoredUserEmail);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const context = useContext(AppContext);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const jsonData = await authApi.login(email, password);

      if (jsonData.jwt && jsonData.user) {
        storeSession({
          jwt: jsonData.jwt,
          user: jsonData.user,
        });

        context?.updateLoginStatus(true);
        navigate("/");
        return;
      }

      setError(jsonData.error || "Failed, try again.");
    } catch (error) {
      if (error instanceof TypeError) {
        setError("Unable to connect to the server.");
      } else {
        setError("Unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const storedJWT = localStorage.getItem("jwt");

  if (storedJWT) {
    navigate("/");
  }

  return (
    <div className="grid min-h-screen bg-bg fade-in lg:grid-cols-[minmax(0,0.95fr)_minmax(420px,0.65fr)]">
      <section className="relative hidden overflow-hidden bg-login p-10 text-white lg:grid lg:content-between xl:p-16">
        <div className="relative z-10">
          <h1
            className="brand-wordmark text-login cursor-pointer text-[78px] leading-none xl:text-[118px]"
            onClick={() => navigate("/")}
          >
            {process.env.REACT_APP_COMPANY_NAME}
          </h1>
          {process.env.REACT_APP_COMPANY_TAGLINE ? (
            <div className="mt-10 flex items-center gap-3 border-b border-white/20 pb-4">
              <span className="h-2 w-2 rounded-full bg-success" />
              <p className="text-lg font-medium text-white">
                {process.env.REACT_APP_COMPANY_TAGLINE}
              </p>
            </div>
          ) : null}
        </div>
        <p className="relative z-10 max-w-xl text-3xl font-semibold leading-tight text-white/70 xl:text-5xl">
          Access the curated library of brand files, press releases, contacts,
          and campaign assets.
        </p>
        <div className="absolute inset-x-10 bottom-10 h-px bg-white/20 xl:inset-x-16" />
      </section>

      <section className="flex min-h-screen flex-col justify-center px-5 py-12 md:px-10 lg:min-h-0">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <h1
              className="brand-wordmark cursor-pointer text-[48px] leading-none"
              onClick={() => navigate("/")}
            >
              {process.env.REACT_APP_COMPANY_NAME}
            </h1>
            <p className="mt-3 text-sm font-semibold uppercase text-muted">
              Brand asset library
            </p>
          </div>

          <div className="brand-panel p-6 md:p-8">
            <div className="mb-8 border-b border-border/35 pb-5">
              <h2 className="text-3xl font-semibold">Sign in</h2>
              <p className="mt-2 text-sm font-medium text-muted">
                Continue to your {process.env.REACT_APP_COMPANY_NAME} workspace.
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-semibold uppercase text-muted"
                >
                  E-mail address
                </label>
                <div className="mt-1">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold uppercase text-muted"
                >
                  Password
                </label>
                <div className="mt-1">
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <div className="flex justify-center">
                  <Spinner className="mt-5 w-9 h-9" />
                </div>
              ) : (
                <div>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    className="w-full"
                  >
                    Sign in
                  </Button>
                  {error && (
                    <p className="mt-3 border-l-2 border-danger bg-danger/10 px-3 py-2 text-sm font-semibold text-danger">
                      {error}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <Link
                    to="/request-password-reset"
                    className="font-semibold text-text underline decoration-2 underline-offset-4 hover:text-accent"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
